import { useState } from "react";
import { C } from "../../shared/theme";
import { todayKey } from "../../shared/dates";
import { EditorFrame, fieldLabel as label, DeleteButton } from "../../shared/EditorFrame";
import { Segmented } from "../../shared/Segmented";
import { goalChoices, startingGoalId } from "../tasks/goalOrder";
import { GoalSelect } from "../tasks/GoalSelect";
import { REPEATS, DEFAULT_REMIND, repeatOf, remindUnitsFor, fitRemind, parseRemind, formatRemind, remindLabel, remindStart, shortDate, goalForNewTasks, upcomingDate } from "./keyDates";

const capital = (s) => s[0].toUpperCase() + s.slice(1);
// At 1 or the maximum the button stays focusable (aria-disabled) and looks unavailable.
const stepButton = (off) => ({ width: 44, height: 44, background: "none", border: "none", borderRadius: 8, cursor: off ? "default" : "pointer", color: C.text, opacity: off ? 0.3 : 1, fontSize: 22, fontWeight: 600, lineHeight: 1, fontFamily: "inherit" });
const input = () => ({ width: "100%", minHeight: 48, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "10px 14px", fontSize: 16, color: C.text, fontFamily: "inherit", boxSizing: "border-box" });

// One sentence under "Start reminding me": when this date reaches Radar.
export const radarSentence = (date, remindBefore, today = todayKey()) => {
  if (!date) return `Reminders start ${remindLabel(remindBefore)} the date.`;
  if (date < today) return "This date has passed, so it won't show on Radar.";
  const start = remindStart(date, remindBefore);
  const before = remindLabel(remindBefore);
  if (start < today) return `It's on Radar now. Reminders started ${before}, on ${shortDate(start)}.`;
  if (start === today) return `It's on Radar from today, ${before}.`;
  return `It will show on Radar ${before}, from ${shortDate(start)}.`;
};

// Adding or editing one Key Date, over the page (the shared EditorFrame). A new one can be given its
// first tasks here; after that they're added on its card. onSave(fields, taskTexts) and onDelete() return
// promises. `taskCount` is how many tasks the date has, for the sentence about deleting it. `goals` are
// the user's goals, for "Goal".
export function KeyDateEditor({ keyDate, taskCount = 0, goals = [], onSave, onDelete, onClose }) {
  const [title, setTitle] = useState(keyDate?.title || "");
  const [date, setDate] = useState(keyDate?.date || "");
  const [repeat, setRepeat] = useState(() => repeatOf(keyDate)?.id || null);
  const repeatInfo = REPEATS.find(r => r.id === repeat);
  const goalOptions = goalChoices(goals, keyDate?.goalId);
  const [goalId, setGoalId] = useState(() => startingGoalId(goalOptions, keyDate?.goalId));
  const newTaskGoal = goalForNewTasks(goalId, goals);
  // A saved reminder too far ahead for the date's repeat (an older version allowed it) opens as one that fits.
  const [remindBefore, setRemindBefore] = useState(() => fitRemind(keyDate?.remindBefore || DEFAULT_REMIND, repeatOf(keyDate)?.id || null));
  const remind = parseRemind(remindBefore);
  // A date that repeats can't be reminded further ahead than the last time (remindUnitsFor).
  const units = remindUnitsFor(repeat);
  const unit = units.find(u => u.id === remind.unit) || units[0];
  // Switching unit keeps the number (3 weeks → 3 months), capped at that unit's maximum.
  const setRemind = (n, unitId) => setRemindBefore(formatRemind(Math.min(Math.max(n, 1), units.find(u => u.id === unitId).max), unitId));
  // A reminder too far ahead for the new repeat becomes that repeat's own (a month ahead → a day, for a weekly date).
  const chooseRepeat = (id) => { const next = id === "once" ? null : id; setRepeat(next); if (next) setRemindBefore(r => fitRemind(r, next)); };
  const [taskTexts, setTaskTexts] = useState([""]);
  const [busy, setBusy] = useState(null); // "save" | "delete"
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const canSave = title.trim() && date && !busy;
  // A repeating date that has passed moves on as soon as it's saved, so Radar's sentence is about the next one.
  const nextDate = repeat && date ? upcomingDate({ date, repeat }) : date;
  const movesNow = nextDate !== date;
  const sentence = radarSentence(nextDate, remindBefore);

  const save = async () => {
    if (!canSave) return;
    setBusy("save");
    setError(null);
    try {
      await onSave({ title: title.trim(), date, remindBefore, repeat, goalId: goalId || null }, taskTexts.map(t => t.trim()).filter(Boolean));
      onClose();
    } catch (err) {
      console.error("Saving a key date failed:", err);
      setError("We couldn't save your key date. Check your connection and try again.");
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setBusy("delete");
    try { await onDelete(); onClose(); }
    catch (err) {
      console.error("Deleting a key date failed:", err);
      setError("We couldn't delete your key date. Check your connection and try again.");
      setBusy(null);
    }
  };

  return (
    <EditorFrame id="key-date-editor-title" title={keyDate ? "Edit key date" : "New key date"}
      canSave={canSave} saving={busy === "save"} busy={!!busy} onSave={save} onClose={onClose}>
      <div>
        <label htmlFor="key-date-title" style={{ ...label(), display: "block" }}>What's happening?</label>
        <input id="key-date-title" value={title} onChange={e => setTitle(e.target.value)} autoFocus={!keyDate}
          placeholder="Fly home for Christmas" style={input()} />
      </div>

      <div>
        <label htmlFor="key-date-date" style={{ ...label(), display: "block" }}>Date</label>
        <input id="key-date-date" className="date-field" type="date" value={date} onChange={e => setDate(e.target.value)} style={input()} />
        <div style={{ marginTop: 10 }}>
          <Segmented label="How often" stretch options={[["once", "Once"], ...REPEATS.map(r => [r.id, r.label])]}
            value={repeat || "once"} onChange={chooseRepeat} />
        </div>
        {repeatInfo && (
          <p style={{ margin: "10px 0 0", fontSize: 15, lineHeight: 1.5, color: C.textMid }}>
            {movesNow ? `This date has passed, so it moves to ${shortDate(nextDate)} when you save.` : `The day after the date, it moves to ${repeatInfo.next}.`} Tasks you've done come back to do, and their deadlines move on with it.
          </p>
        )}
      </div>

      <div role="group" aria-labelledby="key-date-remind">
        <div id="key-date-remind" style={label()}>Start reminding me</div>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
          <div style={{ display: "inline-flex", alignItems: "center", background: C.surface2, borderRadius: 10, padding: 2 }}>
            <button onClick={() => setRemind(remind.n - 1, remind.unit)} aria-disabled={remind.n <= 1} aria-label={`Fewer ${unit.many}`} style={stepButton(remind.n <= 1)}>−</button>
            <span aria-label={`${remind.n} ${remind.n === 1 ? unit.one : unit.many}`}
              style={{ minWidth: 36, textAlign: "center", fontSize: 18, fontWeight: 700, color: C.text, fontVariantNumeric: "tabular-nums" }}>{remind.n}</span>
            <button onClick={() => setRemind(remind.n + 1, remind.unit)} aria-disabled={remind.n >= unit.max} aria-label={`More ${unit.many}`} style={stepButton(remind.n >= unit.max)}>+</button>
          </div>
          {/* A weekly date counts only in days: the word, not a switch with one choice. */}
          {units.length > 1 ? (
            <div style={{ flex: 1, minWidth: 180 }}>
              <Segmented label="Count in" stretch options={units.map(u => [u.id, capital(remind.n === 1 ? u.one : u.many)])}
                value={remind.unit} onChange={(u) => setRemind(remind.n, u)} />
            </div>
          ) : <span style={{ fontSize: 16, color: C.text }}>{remind.n === 1 ? unit.one : unit.many}</span>}
        </div>
        <p aria-live="polite" style={{ margin: "10px 0 0", fontSize: 15, lineHeight: 1.5, color: C.textMid }}>
          {sentence}{repeatInfo && remind.n >= unit.max && ` ${repeatInfo.capWords}`}
        </p>
      </div>

      {goalOptions.length > 0 && (
        <div>
          <label htmlFor="key-date-goal" style={{ ...label(), display: "block" }}>Goal</label>
          <GoalSelect id="key-date-goal" options={goalOptions} value={goalId} onChange={setGoalId} style={input()} />
          {newTaskGoal && (
            <p style={{ margin: "10px 0 0", fontSize: 15, lineHeight: 1.5, color: C.textMid }}>
              Tasks you add to this date start in {newTaskGoal.title}. You can change that on any task.
            </p>
          )}
        </div>
      )}

      {!keyDate && (
        <div role="group" aria-labelledby="key-date-tasks">
          <div id="key-date-tasks" style={label()}>What needs doing before then?</div>
          <div style={{ display: "grid", gap: 8 }}>
            {taskTexts.map((text, i) => (
              <input key={i} value={text} aria-label={`Task ${i + 1}`} placeholder={i === 0 ? "Book the flights" : "Another task"}
                onChange={e => setTaskTexts(ts => ts.map((t, j) => (j === i ? e.target.value : t)))} style={input()} />
            ))}
          </div>
          <button onClick={() => setTaskTexts(ts => [...ts, ""])}
            style={{ marginTop: 6, minHeight: 44, padding: 0, background: "none", border: "none", color: C.textGreen, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
            + Add another task
          </button>
        </div>
      )}

      {error && <p role="alert" style={{ margin: 0, fontSize: 15, lineHeight: 1.5, color: C.red }}>{error}</p>}

      {keyDate && (
        <div>
          <DeleteButton noun="key date" confirming={confirmDelete} deleting={busy === "delete"} disabled={!!busy} onClick={remove} />
          {confirmDelete && taskCount > 0 && (
            <p style={{ margin: "4px 0 0", fontSize: 15, lineHeight: 1.5, color: C.textMid }}>
              {taskCount === 1 ? "Its task stays in Tasks." : `Its ${taskCount} tasks stay in Tasks.`}
            </p>
          )}
        </div>
      )}
    </EditorFrame>
  );
}
